import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { BASE_PATH, isLocalDev } from '@/lib/env'
import { useAuthStore } from '@/stores/auth'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const state = useAuthStore.getState()

    // Zustand persistの初期化完了を待つ
    // localStorageからの復元が完了するまで待機する
    // タイムアウトは5秒（通常は数ミリ秒で完了する）
    const waitForHydration = async (maxWaitTime = 5000) => {
      const startTime = Date.now()
      // _hasHydratedが存在し、かつtrueになるまで待つ
      while (!useAuthStore.getState()._hasHydrated) {
        if (Date.now() - startTime > maxWaitTime) {
          console.warn('Zustand persist hydration timeout')
          break
        }
        await new Promise((resolve) => setTimeout(resolve, 10))
      }
    }

    await waitForHydration()

    // URLパラメータからトークンを取得
    const urlParams = new URLSearchParams(location.search)
    const tokenFromDracaena = urlParams.get('token')
    // 検証の成否にかかわらず、遷移先・ログイン画面の戻り先に token を残さない。dracaena はログイン後に戻り先へ
    // token を付け足すため、残すと token が2つ並び、新しい token も検証に失敗してログインし直しても入れなくなる
    urlParams.delete('token')
    const cleanSearch = urlParams.toString()
    // リダイレクトにはパース済みの値をそのまま渡す。URLSearchParams を通すと数値・真偽値が文字列になり、
    // TanStack Router が文字列として書き出すため URL に引用符が付く（?id=123 → ?id=%22123%22）
    const { token: _token, ...searchWithoutToken }: Record<string, unknown> = location.search

    // dracaenaからのトークンがある場合、既存セッションに関わらず検証と設定を行う
    // これにより、アカウント切り替え時に正しく新しいアカウントでログインできる
    if (tokenFromDracaena) {
      let verified = false

      try {
        const apiUrl = `${BASE_PATH}/api/auth/verify-token`

        // トークンを使用してセッション情報を取得
        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${tokenFromDracaena}`,
          },
        })

        if (response.ok) {
          const sessionData = await response.json()
          // トークンが有効な場合、認証状態を設定
          state.setUser({
            ...sessionData,
            token: tokenFromDracaena,
            expires: sessionData.expiresInDays,
          })

          verified = true
        }
      } catch (error) {
        console.error('Token verification failed:', error)
      }

      // 検証に成功したとき、または失敗してもログイン済みのときは、token を除いた URL へリダイレクトする
      // （catch に握りつぶされないよう try-catch の外で投げる）
      if (verified || state.isAuthenticated()) {
        throw redirect({
          to: location.pathname,
          search: Object.keys(searchWithoutToken).length > 0 ? searchWithoutToken : undefined,
        })
      }
    }

    // 通常の認証チェック
    if (!state.isAuthenticated()) {
      // 環境判定: auth.tsのlogout()と同じパターンを使用
      // - local環境 (localhost): ${BASE_PATH}/login（例: /opportunity-portal/login）
      // - dev/本番環境: /login → nginxがdracaenaのログイン画面にルーティング
      //
      // 注意: TanStack Routerのredirect()を使用すると、BASE_PATH=/opportunity-portalが自動で付与され
      // /opportunity-portal/loginに解決されてしまう。dev/本番環境では/login（dracaena側）に
      // 遷移する必要があるため、window.location.hrefで直接遷移する。
      const loginPath = isLocalDev ? `${BASE_PATH}/login` : '/login'

      // 現在のパスを構築
      // - local: location.pathname（ベースパスなし /opportunities）
      // - dev/本番: window.location.pathname（ベースパス含む /opportunity-portal/opportunities）
      const pathname = isLocalDev ? location.pathname : window.location.pathname
      const search = cleanSearch ? `?${cleanSearch}` : ''
      const currentPath = pathname + search

      window.location.href = `${loginPath}?redirect=${encodeURIComponent(currentPath)}`
      return
    }
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated())

  // 未認証の場合はローディング表示（リダイレクト中）
  if (!isAuthenticated) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900' />
      </div>
    )
  }

  return <Outlet />
}
